import { Building2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Card, DataTable, EmptyState, ErrorState, Input, Skeleton } from '@/components/ui';
import { formatDate, formatNumber, formatRelative } from '@/lib/utils';

import { useBusinesses } from './admin-api';
import type { BusinessRow, RangeDays } from './admin-schemas';
import { BusinessDrawer } from './business-drawer';
import { langLabel, tradeLabel } from './labels';

export function BusinessesSection({ days }: { days: RangeDays }) {
  const { t } = useTranslation('admin');
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  // חיפוש אחרי הפסקת הקלדה — כל בקשה נרשמת ביומן הביקורת של המפעיל.
  useEffect(() => {
    const id = setTimeout(() => setSearch(input.trim()), 350);
    return () => clearTimeout(id);
  }, [input]);

  const q = useBusinesses(days, search, true);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="relative block w-full max-w-sm">
          <span className="sr-only">{t('businesses.search')}</span>
          <Search
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle"
            aria-hidden
          />
          <Input
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('businesses.searchPlaceholder')}
            maxLength={100}
            className="ps-9"
          />
        </label>
        {q.data ? (
          <p className="text-xs text-fg-muted">
            {q.data.total > q.data.rows.length
              ? t('businesses.shown', {
                  shown: formatNumber(q.data.rows.length),
                  total: formatNumber(q.data.total),
                })
              : t('businesses.count', { count: q.data.total })}
          </p>
        ) : null}
      </div>

      <Card className="overflow-hidden">
        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : !q.data ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : q.data.rows.length === 0 ? (
          <EmptyState icon={Building2} title={t('businesses.empty')} />
        ) : (
          <DataTable<BusinessRow>
            caption={t('businesses.caption')}
            minWidth="64rem"
            rows={q.data.rows}
            rowKey={(r) => r.id}
            columns={[
              {
                key: 'name',
                header: t('businesses.columns.name'),
                cell: (r) => (
                  <button
                    type="button"
                    onClick={() => setOpenId(r.id)}
                    aria-label={t('businesses.open', { name: r.name })}
                    className="text-start font-medium text-accent hover:underline focus-visible:outline-accent"
                  >
                    {r.name}
                    <span dir="ltr" className="block text-2xs font-normal text-fg-subtle">
                      {r.subdomain}
                    </span>
                  </button>
                ),
              },
              {
                key: 'trade',
                header: t('businesses.columns.trade'),
                cell: (r) => (
                  <span className="flex flex-wrap items-center gap-1">
                    <Badge tone="violet" dot={false}>
                      {tradeLabel(r.trade)}
                    </Badge>
                    {r.deletionScheduledFor ? (
                      <Badge tone="danger">{t('status.pendingDeletion')}</Badge>
                    ) : !r.isActive ? (
                      <Badge tone="danger">{t('businesses.inactive')}</Badge>
                    ) : null}
                  </span>
                ),
              },
              {
                key: 'lang',
                header: t('businesses.columns.language'),
                cell: (r) => langLabel(r.locale),
              },
              {
                key: 'users',
                header: t('businesses.columns.users'),
                numeric: true,
                cell: (r) => formatNumber(r.users),
              },
              {
                key: 'active',
                header: t('businesses.columns.activeUsers'),
                numeric: true,
                cell: (r) => formatNumber(r.activeUsers),
              },
              {
                key: 'created',
                header: t('businesses.columns.created'),
                cell: (r) => formatDate(r.createdAt),
              },
              {
                key: 'lastActive',
                header: t('businesses.columns.lastActive'),
                cell: (r) =>
                  r.lastActiveAt ? formatRelative(r.lastActiveAt) : t('businesses.never'),
              },
              {
                key: 'tasks',
                header: t('businesses.columns.tasks'),
                numeric: true,
                cell: (r) => formatNumber(r.tasks),
              },
              {
                key: 'quotes',
                header: t('businesses.columns.quotes'),
                numeric: true,
                cell: (r) => formatNumber(r.quotes),
              },
              {
                key: 'customers',
                header: t('businesses.columns.customers'),
                numeric: true,
                cell: (r) => formatNumber(r.customers),
              },
            ]}
          />
        )}
      </Card>

      <BusinessDrawer businessId={openId} days={days} onClose={() => setOpenId(null)} />
    </div>
  );
}
